import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Config } from 'core/commonTypes';
import { useOptionalUser } from '../UserContext';
import ConsentPopup from './ConsentPopup';
import { useLanguageProvider } from 'components/LanguageSelector/LanguageProvider';

declare global {
  interface Window {
    dataLayer?: any[];
  }
}

type AnalyticsProps = {
  config: Config;
  children?: React.ReactNode;
};

export type AnalyticsControls = {
  analyticsConsent: boolean | null;
  setAnalyticsConsent: (givesConsent: boolean) => void;
  hasAnalyticsCapability: boolean;
  reloadNeeded: boolean;
  pendoPrivacyUrl: string;
};

const LOCAL_STORAGE_KEY = 'slm_analytics_consent' as const;

const AnalyticsContext = createContext<AnalyticsControls>(null);

// Pendo snippet, with modification: Return the <script> element created to inject onload event
const insertPendoScriptTag = function (apiKey: string): HTMLScriptElement {
  return (function (p, e, n, d, o) {
    let v, w, x, y, z;
    o = p[d] = p[d] || {};
    o._q = o._q || [];
    v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
    for (w = 0, x = v.length; w < x; ++w)
      (function (m) {
        o[m] =
          o[m] ||
          function () {
            o._q[m === v[0] ? 'unshift' : 'push'](
              [m].concat([].slice.call(arguments, 0))
            );
          };
      })(v[w]);
    y = e.createElement(n);
    y.async = !0;
    y.src = 'https://cdn.pendo.io/agent/static/' + apiKey + '/pendo.js';
    z = e.getElementsByTagName(n)[0];
    z.parentNode.insertBefore(y, z);
    return y;
  })(window, document, 'script', 'pendo');
};

async function hashMail(mail: string): Promise<string> {
  const textAsBuffer = new TextEncoder().encode(mail);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', textAsBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  return hashArray.map(item => item.toString(16).padStart(2, '0')).join('');
}

const Analytics: React.FC<AnalyticsProps> = ({ config, children }) => {
  const user = useOptionalUser();
  const { locale } = useLanguageProvider();
  const [consent, setConsent] = useState<Gtag.ConsentParams | null>(() => {
    const storedConsent = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    return storedConsent === null ? null : JSON.parse(storedConsent); // intentionally allow null to check if user made active choice
  });
  const [gaInitialized, setGaInitialized] = useState(false);
  const [gaEnabled, setGaEnabled] = useState(false);

  const [pendoLoaded, setPendoLoaded] = useState(false);
  const [pendoEnabled, setPendoEnabled] = useState(false);
  // TBD if Pendo can be disabled, until then inform user of necessary reload
  const [reloadNeeded, setReloadNeeded] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' || !config.ga?.tag) {
      return;
    }

    if (!gaInitialized && config && user && typeof window.gtag !== 'function') {
      if (!user.country || config.ga.countryExclusions.includes(user.country)) {
        setGaInitialized(true);
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      window.gtag(
        'consent',
        'default',
        consent || {
          ad_storage: 'denied',
          analytics_storage: 'denied',
        }
      );
      window.gtag('js', new Date());
      window.gtag('config', config.ga.tag);
      const script = window.document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${config.ga.tag}`;
      window.document.body.appendChild(script);
      setGaInitialized(true);
      setGaEnabled(true);
    }
  }, [config, user, consent, gaInitialized, gaEnabled]);

  // Load the Pendo snippet for allowed users
  useEffect(() => {
    if (
      process.env.NODE_ENV === 'development' ||
      !config.pendo?.key ||
      pendoLoaded
    ) {
      return;
    }

    if (!pendoLoaded && config && user) {
      if (
        config.pendo.approvedCountries !== '*' &&
        (!user.country ||
          !config.pendo.approvedCountries.includes(user.country))
      ) {
        return;
      }

      const scriptTag = insertPendoScriptTag(config.pendo.key);
      scriptTag.addEventListener('load', () => {
        setPendoLoaded(true);
      });
    }
  }, [config, pendoLoaded, user]);

  // After Pendo snippet is loaded, enable when given consent.
  useEffect(() => {
    if (
      pendoLoaded &&
      !pendoEnabled &&
      consent?.analytics_storage === 'granted'
    ) {
      const mail = user.userMail || user.userPrincipalName;
      if (!mail) return; // User has no mail-like property, give up analytics
      hashMail(mail).then(hashedEmail => {
        (window as any).pendo.initialize({
          visitor: {
            id: hashedEmail,
            jobtitle: user.jobTitle,
            department: user.department,
            primarysite: user.primarySite,
            country: user.country,
            businessunit: user.businessUnit,
            role: 'application_user',
          },
        });
        setPendoEnabled(true);
      });
    }
  }, [consent?.analytics_storage, pendoEnabled, pendoLoaded, user]);

  // Update pendo language configuration according to user setting
  useEffect(() => {
    // Check that the pendo function exists. Seems to be a staging issue where it's replaced
    if (
      pendoEnabled &&
      typeof (window as any).pendo.getSerializedMetadata === 'function'
    ) {
      const pendoMetadata = (window as any).pendo.getSerializedMetadata();

      (window as any).pendo.updateOptions({
        ...pendoMetadata,
        visitor: {
          ...pendoMetadata.visitor,
          selectedlanguage: locale,
        },
      });
    }
  }, [locale, pendoEnabled]);

  const setConsentFn = useCallback(
    (givesConsent: boolean) => {
      const consentValue: Gtag.ConsentParams = {
        ad_storage: 'denied',
        analytics_storage: givesConsent ? 'granted' : 'denied',
      };
      setConsent(consentValue);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(consentValue));
      if (window.gtag) {
        window.gtag('consent', 'update', consentValue);
      }
      setReloadNeeded(
        consent?.analytics_storage === 'granted' && !givesConsent
      );
    },
    [consent?.analytics_storage]
  );

  const analyticsValue: AnalyticsControls = {
    analyticsConsent: consent?.analytics_storage === 'granted',
    setAnalyticsConsent: setConsentFn,
    hasAnalyticsCapability: (gaInitialized && gaEnabled) || pendoLoaded,
    reloadNeeded,
    pendoPrivacyUrl: pendoLoaded && config.pendo?.privacyDocument,
  };

  return (
    <AnalyticsContext.Provider value={analyticsValue}>
      <ConsentPopup
        hasSetConsent={consent !== null}
        givesConsent={consent?.analytics_storage === 'granted' || false}
        setConsent={setConsentFn}
      />
      {children}
    </AnalyticsContext.Provider>
  );
};

export default Analytics;

export function useAnalytics(): AnalyticsControls | null {
  const analytics = useContext(AnalyticsContext);
  if (analytics === null) {
    throw new Error('Analytics hook used outside of AnalyticsContext');
  }
  return analytics;
}
